import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import nurseryQuarantineService from '@/services/nurseryQuarantine';
import { useModuleLicense } from '@/hooks/useModuleLicense';
import { errorMessage } from '@/utils/apiResponse';
import { useOrgTimezone, formatDateTimeInTz } from '@/utils/timezone';
import { QUARANTINE_METHOD_LABEL } from '@/types/nurseryQuarantine';
import type { SprayQuarantine } from '@/types/nurseryQuarantine';
import QuarantineOrderSignoffs from '@/components/nursery/despatch/QuarantineOrderSignoffs';

/**
 * On a spray that carries out an ICA quarantine treatment: which load and
 * protocol it is for, where it is done, and each customer's order for the
 * person applying it to sign off as treated (Rob, 2026-10-14). Someone else
 * validates the treatment on the load page once every order is signed off.
 *
 * Renders nothing for any other spray, or if the check fails: it must never
 * get in the way of running a spray.
 */
function SprayQuarantineCard({ sprayId, sprayStatus }: { sprayId: number; sprayStatus: string }) {
  const tz = useOrgTimezone();
  const { hasAccess: hasSpraySeat } = useModuleLicense('spray', ['operator', 'creator', 'admin']);
  const [data, setData] = useState<SprayQuarantine | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await nurseryQuarantineService.getSprayQuarantine(sprayId);
      if (res.success) setData(res.data ?? null);
    } catch { /* not a quarantine spray, or no nursery access: show nothing */ }
  }, [sprayId]);

  useEffect(() => { load(); }, [load, sprayStatus]);

  if (!data) return null;

  const change = async (orderId: number, undo: boolean) => {
    setBusy(orderId);
    setError(null);
    try {
      const res = undo
        ? await nurseryQuarantineService.undoSignOffOrder(data.treatment_id, orderId)
        : await nurseryQuarantineService.signOffOrder(data.treatment_id, orderId);
      if (res.success && res.data) setData(res.data);
      else setError(errorMessage(res, 'Could not sign the order off'));
    } catch (e) {
      setError(errorMessage(e, 'Could not sign the order off'));
    } finally {
      setBusy(null);
    }
  };

  const toSign = data.orders.filter((o) => !o.gone);
  const signed = toSign.filter((o) => o.signed_at).length;
  const allSigned = toSign.length > 0 && signed === toSign.length;
  const method = data.method && data.method !== 'all' ? QUARANTINE_METHOD_LABEL[data.method] : null;

  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3" aria-labelledby="spray-quarantine">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id="spray-quarantine" className="flex items-center gap-2 text-sm font-semibold text-neutral-600 uppercase tracking-wide">
          <ShieldCheck className="h-4 w-4" /> Quarantine treatment
        </h3>
        {data.order_signoff && (
          <span className="text-sm text-neutral-600">{`${signed} of ${toSign.length} order${toSign.length === 1 ? '' : 's'} signed off`}</span>
        )}
      </div>
      <p className="text-sm text-neutral-700 break-words">
        {`${data.code} · ${data.name}${method ? ` · ${method}` : ''}, for load `}
        <Link to={`/nursery/loads/${data.load_id}`} className="underline font-medium">{data.load_number}</Link>
        {data.area_name ? `. Treated at ${data.area_name}.` : '.'}
      </p>

      {data.order_signoff ? (
        <>
          <p className="text-xs text-neutral-500">
            {data.validated_at
              ? `Validated by ${data.validated_by_name || 'someone'} ${formatDateTimeInTz(data.validated_at, tz)}. The sign-offs can no longer change.`
              : data.can_sign
                ? 'Sign each customer\'s order off as its trees are treated. Someone other than you then validates the treatment.'
                : data.state === 'cancelled'
                  ? 'The spray was cancelled, so nothing was treated.'
                  : 'Start the spray, then sign each customer\'s order off as its trees are treated.'}
          </p>
          <QuarantineOrderSignoffs
            rows={data.orders}
            tz={tz}
            canSign={data.can_sign && hasSpraySeat}
            busyOrderId={busy}
            onSign={(id) => change(id, false)}
            onUndo={(id) => change(id, true)}
          />
          {allSigned && !data.validated_at && (
            <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              Every order is signed off. Someone other than whoever applied it validates the treatment on the load page.
            </p>
          )}
        </>
      ) : (
        <p className="text-xs text-neutral-500">Made before orders were signed off one by one: it is validated on the load page.</p>
      )}
      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{error}</div>}
    </section>
  );
}

export default SprayQuarantineCard;
